import type { GoalType } from './life-events';

export type ScenarioIntentTargetSection =
  | 'cashflow'
  | 'portfolio'
  | 'life-events'
  | 'retirement-tax';

export type ScenarioIntentId =
  | 'budget-change'
  | 'income-change'
  | 'market-change'
  | 'large-purchase'
  | 'large-windfall'
  | 'something-else';

export interface ScenarioIntentOption {
  id: ScenarioIntentId;
  label: string;
  detail: string;
  iconName: string;
  targetSection: ScenarioIntentTargetSection | null;
  namePlaceholder: string;
  lifeEventType?: GoalType;
  lifeEventHint?: string;
}

export const SCENARIO_INTENTS: ScenarioIntentOption[] = [
  {
    id: 'budget-change',
    label: 'Budget change',
    detail: 'Spend more or less each month',
    iconName: 'wallet-outline',
    targetSection: 'cashflow',
    namePlaceholder: 'e.g. Lower travel spending',
  },
  {
    id: 'income-change',
    label: 'Income change',
    detail: 'Raise, job change, side income, or work break',
    iconName: 'cash-outline',
    targetSection: 'cashflow',
    namePlaceholder: 'e.g. Consulting income',
  },
  {
    id: 'market-change',
    label: 'Market change',
    detail: 'Portfolio shift or largest holding price',
    iconName: 'trending-up-outline',
    targetSection: 'portfolio',
    namePlaceholder: 'e.g. Stock price reset',
  },
  {
    id: 'large-purchase',
    label: 'Large purchase',
    detail: 'Second home, car, renovation, or tuition',
    iconName: 'home-outline',
    targetSection: 'life-events',
    namePlaceholder: 'e.g. Second home',
    lifeEventType: 'expense',
    lifeEventHint:
      'Add the purchase, down payment, or recurring cost this scenario should include.',
  },
  {
    id: 'large-windfall',
    label: 'Large windfall',
    detail: 'Inheritance, IPO, bonus, or asset sale',
    iconName: 'gift-outline',
    targetSection: 'life-events',
    namePlaceholder: 'e.g. Private stock IPO',
    lifeEventType: 'inheritance',
    lifeEventHint:
      'Add the one-time money event this scenario should include.',
  },
  {
    id: 'something-else',
    label: 'Something else',
    detail: 'Start from the full scenario editor',
    iconName: 'ellipsis-horizontal-circle-outline',
    targetSection: null,
    namePlaceholder: 'e.g. Move to Texas',
  },
];

export function getScenarioIntentOption(
  raw: string | string[] | undefined,
): ScenarioIntentOption | null {
  const id = Array.isArray(raw) ? raw[0] : raw;
  if (!id) return null;
  return SCENARIO_INTENTS.find((option) => option.id === id) ?? null;
}
